import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'recompose';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { Alert } from 'reactstrap';
import Spinner from '../../components/spinner';
import HttpError from '../../components/http-error';
import withAPI from '../../hocs/with-api';
import '../../date-picker.css';
import 'react-datepicker/dist/react-datepicker.css';
import Table from '../../components/table';
import ActionCard from '../../components/action-card';
import DropdownFilter from '../../components/dropdown-filter';
import ButtonWithIcon from '../../components/button-with-icon';
import ReportVehiclesWithoutGeoZoneEventRow from '../../components/report-vehicles-without-geo-zone-event-row';
import Pagination, { getCurrentPageNumber } from '../../components/pagination';

const MAX_DATE_RANGE_MONTHS = 2;

class ReportVehiclesWithoutGeoZoneEvent extends PureComponent {
  constructor(props) {
    super(props);
    const { startDate, endDate, carrierId } = this.getFiltersFromQuery(props);
    this.state = {
      reportData: [],
      startDate,
      endDate,
      isLoading: false,
      hasError: false,
      hasData: false,
      dateRangeError: null,
      carrierId,
      isExporting: false,
      carriers: [],
      currentPage: getCurrentPageNumber(props),
    };
  }

  getFiltersFromQuery = (props) => {
    const sp = new URLSearchParams(props.location.search);

    const start = sp.get('start-date');
    const end = sp.get('end-date');
    const carrierId = sp.get('carrier-id');
    const defaultEndDate = moment().toDate();
    const defaultStartDate = moment().subtract(6, 'days').toDate();
    return {
      startDate: start ? moment(start, 'YYYY-MM-DD').toDate() : defaultStartDate,
      endDate: end ? moment(end, 'YYYY-MM-DD').toDate() : defaultEndDate,
      carrierId: carrierId ? parseInt(carrierId, 10) : '',
    };
  };

  async componentDidMount() {
    const { t } = this.props;
    window.onpopstate = this.onBackButtonClick;
    document.title = `${t('reports.vehiclesWithoutGeoZoneEventTitle')} - ${t(
      'app.name',
    )}`;
    await this.loadCarriers();

    const { startDate, endDate } = this.state;
    if (startDate && endDate) {
      await this.loadReportData();
    }
  }

  componentDidUpdate(prevProps) {
    const { location } = this.props;
    const prevPath = prevProps.location.pathname;
    const nextPath = location.pathname;

    if (prevPath !== nextPath && nextPath === '/reports/vehicles-without-geo-zone-event') {
      const endDate = moment().toDate();
      const startDate = moment(endDate).subtract(6, 'days').toDate();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(
        {
          startDate,
          endDate,
          carrierId: null,
          reportData: [],
          currentPage: 1,
          hasData: false,
        },
        this.loadReportData,
      );
    }
  }

  isHardReload = () => {
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    if (nav && nav.type) return nav.type === 'reload';
    return performance.navigation?.type === 1;
  };

  getMaxEndDate = (startDate) => {
    if (!startDate) return moment().toDate();
    const maxEndDate = moment(startDate).add(MAX_DATE_RANGE_MONTHS, 'months');
    const today = moment();
    return maxEndDate.isBefore(today) ? maxEndDate.toDate() : today.toDate();
  };

  isDateRangeValid = (startDate, endDate) => {
    if (!startDate || !endDate) return true;
    const diffInMonths = moment(endDate).diff(
      moment(startDate),
      'months',
      true,
    );
    return diffInMonths <= MAX_DATE_RANGE_MONTHS;
  };

  getDateRangeErrorMessage = () => {
    const { t } = this.props;
    return t(
      'reports.maxDateRangeNote',
      `Date range cannot exceed ${MAX_DATE_RANGE_MONTHS} months`,
      { maxMonths: MAX_DATE_RANGE_MONTHS },
    );
  };

  loadReportData = async () => {
    const { httpRequest } = this.props;
    const { history } = this.props;
    const {
      startDate, endDate, carrierId, currentPage,
    } = this.state;

    if (!startDate || !endDate) {
      return;
    }

    if (!this.isDateRangeValid(startDate, endDate)) {
      this.setState({
        dateRangeError: this.getDateRangeErrorMessage(),
        hasData: false,
      });
      return;
    }

    const params = {
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
      page: currentPage,
    };

    if (carrierId) {
      params.carrier_id = carrierId;
    }

    this.setState({
      isLoading: true,
      hasError: false,
      dateRangeError: null,
    });

    const searchParams = new URLSearchParams();
    searchParams.set('start-date', moment(startDate).format('YYYY-MM-DD'));
    searchParams.set('end-date', moment(endDate).format('YYYY-MM-DD'));
    if (carrierId) searchParams.set('carrier-id', String(carrierId));

    history.push({
      pathname: `/reports/vehicles-without-geo-zone-event/${currentPage}`,
      search: `?${searchParams.toString()}`,
    });
    try {
      const response = await httpRequest({
        method: 'get',
        url: 'vehicles/geo-zone-report',
        data: { params },
      });

      if (response) {
        const {
          data: reportData,
        } = response;
        this.setState({
          reportData,
          totalPages: response?.data?.last_page,
          totalRecords: response?.data?.total,
          perPage: response?.data?.per_page,
          from: response?.data?.from,
          to: response?.data?.to,
          isLoading: false,
          hasError: false,
          hasData: true,
        });
      }
    } catch (error) {
      console.error('API Error:', error);
      this.setState({
        isLoading: false,
        hasError: true,
        hasData: false,
      });
    }
  };

  handlePageChange = (currentPage) => {
    this.setState(
      {
        currentPage,
      },
      () => {
        this.loadReportData();
      },
    );
  };

   onBackButtonClick = () => {
     const page = getCurrentPageNumber(this.props);
     this.setState({
       currentPage: page,
     }, this.loadReportData);
   }

  onExportClick = async () => {
    const { httpRequest } = this.props;
    const { startDate, endDate, carrierId } = this.state;

    if (!startDate || !endDate) {
      return;
    }

    const params = {
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
      export: true,
    };

    if (carrierId) {
      params.carrier_id = carrierId;
    }

    this.setState({ isExporting: true });

    try {
      const response = await httpRequest({
        method: 'get',
        url: 'vehicles/geo-zone-report',
        data: { params },
        responseType: 'blob',
      });

      if (response) {
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'text/csv',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // eslint-disable-next-line max-len
        const filename = `vehicles-geo-zone-report-${moment(startDate).format(
          'YYYY-MM-DD',
        )}-to-${moment(endDate).format('YYYY-MM-DD')}.csv`;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export Error:', error);
    } finally {
      this.setState({ isExporting: false });
    }
  };

  handleSetStartDate = (date) => {
    const { endDate } = this.state;
    let newEndDate = endDate;
    if (
      endDate
      && date
      && (!this.isDateRangeValid(date, endDate) || date > endDate)
    ) {
      newEndDate = null;
    }

    this.setState(
      {
        startDate: date,
        endDate: newEndDate,
        dateRangeError: null,
        currentPage: 1,
      },
      () => {
        if (date && newEndDate) {
          this.loadReportData();
        } else {
          this.setState({ hasData: false, reportData: [] });
        }
      },
    );
  };

  handleSetEndDate = (date) => {
    const { startDate } = this.state;

    if (startDate && date && !this.isDateRangeValid(startDate, date)) {
      this.setState({
        dateRangeError: this.getDateRangeErrorMessage(),
      });
      return;
    }

    this.setState(
      {
        endDate: date,
        dateRangeError: null,
        currentPage: 1,
      },
      () => {
        if (startDate && date) {
          this.loadReportData();
        }
      },
    );
  };

  onCarrierSelected = (carrierId) => {
    this.setState(
      {
        carrierId,
        currentPage: 1,
      },
      () => {
        const { startDate, endDate } = this.state;
        if (startDate && endDate) {
          this.loadReportData();
        }
      },
    );
  };

  async loadCarriers() {
    const { httpRequest } = this.props;
    const { data: carriers } = await httpRequest({
      method: 'get',
      url: 'carriers',
      tag: 'carriers',
    });
    if (carriers) {
      this.setState({
        carriers,
      });
    }
  }

  render() {
    const { t } = this.props;
    const {
      reportData,
      startDate,
      endDate,
      isLoading,
      hasError,
      hasData,
      dateRangeError,
      isExporting,
      carriers,
      carrierId,
      totalPages,
      currentPage,
      perPage,
      totalRecords,
      from,
      to,
    } = this.state;

    const filterOptions = _.map(carriers, (carrier) => ({
      id: carrier.id,
      label: carrier.name,
      value: carrier.id,
    }));

    const carrierFilter = (
      <div className="inline-block">
        <DropdownFilter
          dropDownStyles={{ toggleStyles: { maxWidth: '240px' } }}
          options={filterOptions}
          label={t('carriers.one')}
          onSelected={this.onCarrierSelected}
          selectedValue={carrierId}
        />
      </div>
    );

    return (
      <>
        <ActionCard
          title={t('reports.report')}
          withActionButton={false}
          headerChildren={[
            <div className="inline-block mr-2 mb-2">{carrierFilter}</div>,
            <div className="inline-block mr-2 mb-2">
              <DatePicker
                onChange={this.handleSetStartDate}
                selected={startDate}
                className="input-clean center-text kpi-date-picker"
                dateFormat="dd/MM/yyyy"
                maxDate={moment().toDate()}
                placeholderText={t('reports.selectStartDate')}
              />
            </div>,
            <div className="inline-block mb-2">
              <DatePicker
                onChange={this.handleSetEndDate}
                selected={endDate}
                className="input-clean center-text kpi-date-picker"
                dateFormat="dd/MM/yyyy"
                minDate={startDate}
                maxDate={this.getMaxEndDate(startDate)}
                placeholderText={t('reports.selectEndDate')}
              />
            </div>,
            <ButtonWithIcon
              disabled={_.isEmpty(reportData?.data) || isExporting}
              onClick={this.onExportClick}
              icon="/assets/save-icon.png"
              title={t('kpi.export')}
            />,
          ]}
        >
          <>
            {dateRangeError && (
              <Alert color="warning" className="mb-3">
                {dateRangeError}
              </Alert>
            )}

            {(!startDate || !endDate) && !dateRangeError && (
              <div>
                <p>{t('reports.selectBothStartAndEndDatesToViewReport')}</p>
                <small className="text-muted">
                  {t(
                    'reports.maxDateRangeNote',
                    `Note: Maximum date range is ${MAX_DATE_RANGE_MONTHS} months`,
                    { maxMonths: MAX_DATE_RANGE_MONTHS },
                  )}
                </small>
              </div>
            )}

            {startDate && endDate && !dateRangeError && (
              <>
                {isLoading && <Spinner />}
                {hasError && (
                  <HttpError
                    withTryAgainButton
                    onTryAgain={this.loadReportData}
                  />
                )}
                {!isLoading && !hasError && hasData && (
                  <>
                    <Table
                      hover
                      responsive="xl"
                      size="sm"
                      isEmpty={_.isEmpty(reportData?.data)}
                    >
                      <thead>
                        <tr>
                          <th>{t('orders.orderNumber')}</th>
                          <th>{t('vehicle')}</th>
                          <th>{t('clients.shortName')}</th>
                          <th>{t('delivery.address.end')}</th>
                          <th>{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {_.map(reportData?.data, (order) => (
                          <ReportVehiclesWithoutGeoZoneEventRow
                            key={`itemDataRow-${order?.driver_id}-${order?.id}`}
                            item={order}
                          />
                        ))}
                      </tbody>
                    </Table>
                    {totalPages && (
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onChange={this.handlePageChange}
                        totalRecords={totalRecords}
                        perPage={perPage}
                        from={from}
                        to={to}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </>
        </ActionCard>
      </>
    );
  }
}

ReportVehiclesWithoutGeoZoneEvent.propTypes = {
  t: PropTypes.func.isRequired,
  httpRequest: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

export default compose(
  withTranslation(),
  withAPI,
  withRouter,
)(ReportVehiclesWithoutGeoZoneEvent);
